import { clampDescription } from "@/lib/seo-quality";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Plus, ArrowLeft, Flame, Clock, TrendingUp } from "lucide-react";
import { listForumPosts } from "@/lib/forum.functions";
import { getUnreadThreads } from "@/lib/forum-reads.functions";
import { PostCard, type ForumPostRow } from "@/components/forum/PostCard";
import { useAuth } from "@/hooks/use-auth";
import { FollowButton } from "@/components/forum/FollowButton";
import { FyndanuForumCard } from "@/components/forum/FyndanuForumCard";
import { CategoryGuide } from "@/components/forum/CategoryGuide";


export const Route = createFileRoute("/forum/k/$slug")({
  loader: async ({ params }) => {
    const res = await listForumPosts({ data: { category: params.slug, sort: "hot", limit: 40 } });
    if (!res.category) throw notFound();
    return res;
  },
  head: ({ loaderData }) => {
    if (!loaderData?.category) {
      return { meta: [{ title: "Kategorin hittades inte – Fritidsforum" }, { name: "robots", content: "noindex" }] };
    }
    const name = loaderData.category.name;
    // Kategoritexterna i databasen är korta ledtexter för UI:t — för SERP
    // behövs en fullständig mening, annars blir beskrivningen för tunn.
    const short = loaderData.category.description?.trim();
    const desc = clampDescription(
      short && short.length >= 90
        ? short
        : `${short ? `${short.replace(/[.\s]+$/, "")}. ` : ""}Diskussioner, frågor och svar om ${name.toLowerCase()} i Fritidsforum – AK Husbil & Fritid, Kalix.`,
    );

    const url = `https://www.akhusbilfritid.se/forum/k/${loaderData.category.slug}`;
    return {
      meta: [
        { title: `${name} – Fritidsforum | AK Husbil & Fritid` },
        { name: "description", content: desc },
        { property: "og:title", content: `${name} – Fritidsforum` },
        { property: "og:description", content: desc },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:image", content: "https://www.akhusbilfritid.se/forum-og.jpg" },
        { property: "og:image:alt", content: `${name} – Fritidsforum` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: "https://www.akhusbilfritid.se/forum-og.jpg" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  errorComponent: () => <Fallback title="Något gick fel" />,
  notFoundComponent: () => <Fallback title="Kategorin finns inte" />,
  component: CategoryPage,
});

function Fallback({ title }: { title: string }) {
  return (
    <section className="bg-warm">
      <div className="container-edge py-24 text-center">
        <h1 className="font-display text-4xl">{title}</h1>
        <Link to="/forum" className="mt-6 inline-flex items-center gap-2 text-accent hover:underline">
          <ArrowLeft size={15} /> Till forumet
        </Link>
      </div>
    </section>
  );
}

const PAGE = 20;

function CategoryPage() {
  const initial = Route.useLoaderData();
  const { slug } = Route.useParams();
  const fn = useServerFn(listForumPosts);
  const unreadFn = useServerFn(getUnreadThreads);
  const { signedIn } = useAuth();
  const [sort, setSort] = useState<"hot" | "new" | "top">("hot");
  const [limit, setLimit] = useState(PAGE);

  const { data, isFetching } = useQuery({
    queryKey: ["forum", "posts", slug, sort, limit],
    queryFn: () => fn({ data: { category: slug, sort, limit } }),
    initialData: sort === "hot" && limit === PAGE ? undefined : undefined,
    placeholderData: (prev) => prev,
  });

  const category = data?.category ?? initial.category;
  const rows = (data?.posts ?? (initial.posts as ForumPostRow[]).slice(0, limit)) as ForumPostRow[];
  const total = (data as any)?.total ?? (initial as any).total ?? rows.length;

  const { data: unreadData } = useQuery({
    queryKey: ["forum", "unread", rows.map((r) => r.id).join(",")],
    queryFn: () => unreadFn({ data: { post_ids: rows.slice(0, 100).map((r) => r.id) } }),
    enabled: signedIn && rows.length > 0,
    staleTime: 60_000,
  });
  const unread = new Set(unreadData?.unread ?? []);


  return (
    <section className="bg-warm">
      <div className="container-edge py-12 md:py-20">
        <Link to="/forum" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={15} /> Fritidsforum
        </Link>

        <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <h1 className="font-display text-4xl leading-tight sm:text-5xl md:text-6xl">{category?.name}</h1>
            {category?.description && (
              <p className="mt-3 text-base leading-relaxed text-muted-foreground md:text-lg">
                {category.description}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {category?.id && <FollowButton categoryId={category.id} label="Bevaka kategorin" />}
            <Link
              to={signedIn ? "/forum/nytt" : "/login"}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              <Plus size={16} /> Nytt inlägg
            </Link>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-2">
          {(
            [
              { key: "hot", label: "Hetast", icon: Flame },
              { key: "new", label: "Senaste", icon: Clock },
              { key: "top", label: "Toppade", icon: TrendingUp },
            ] as const
          ).map((s) => (
            <button
              key={s.key}
              onClick={() => setSort(s.key)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition ${
                sort === s.key
                  ? "border-transparent bg-primary text-primary-foreground"
                  : "border-border bg-card text-foreground/80 hover:bg-secondary"
              }`}
            >
              <s.icon size={14} /> {s.label}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-3">
          {!rows.length && (
            <p className="rounded-2xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
              Inga trådar i den här kategorin ännu.
            </p>
          )}
          {rows.map((p) => (
            <PostCard key={p.id} post={p} unread={unread.has(p.id)} />
          ))}
        </div>

        {rows.length < total && (
          <div className="mt-8 text-center">
            <button
              onClick={() => setLimit((l) => l + PAGE)}
              disabled={isFetching}
              className="rounded-full border border-border bg-card px-6 py-3 text-sm font-semibold transition hover:bg-secondary disabled:opacity-60"
            >
              {isFetching ? "Hämtar…" : `Visa fler trådar (${total - rows.length} kvar)`}
            </button>
          </div>
        )}

        <CategoryGuide slug={category?.slug} name={category?.name} />

        <FyndanuForumCard
          context={`${category?.name ?? ""} ${category?.description ?? ""}`}
          className="mt-8"
        />

      </div>

    </section>
  );
}
